import React, { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { getOperators, createOperator, updateOperator, deleteOperator, getStaffUsers } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Trash2, Pencil, UserCog, Link2 } from "lucide-react";
import { useT } from "@/i18n/I18nContext";

const empty = { id: null, name: "", title: "", notes: "", linked_user_id: null, active: true };
const NONE = "__none__";

export default function Operators() {
  const { t } = useT();
  const { can } = useAuth();
  const canManage = can("admin", "manager");
  const [operators, setOperators] = useState([]);
  const [users, setUsers] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = async () => setOperators(await getOperators());
  useEffect(() => {
    (async () => {
      await load();
      if (canManage) {
        try { setUsers(await getStaffUsers()); }
        catch (e) { console.error("Operators: failed to load staff users for linking", e); }
      }
    })();
  }, [canManage]);

  const save = async (f) => {
    try {
      if (!f.name?.trim()) { toast.error(t("operators.saveFailed")); return; }
      const payload = {
        name: f.name, title: f.title, notes: f.notes,
        linked_user_id: f.linked_user_id || null, active: f.active,
      };
      if (f.id) await updateOperator(f.id, payload);
      else await createOperator(payload);
      toast.success(f.id ? t("operators.updated") : t("operators.created"));
      setDialog(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("operators.saveFailed")); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("operators.confirmDelete"))) return;
    try { await deleteOperator(id); toast.success(t("operators.deleted")); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("common.failed")); }
  };

  return (
    <Layout actions={
      canManage ? (
        <Button size="sm" onClick={() => setDialog({ ...empty })} data-testid="add-operator-button">
          <Plus className="mr-1.5 h-4 w-4" /> {t("operators.add")}
        </Button>
      ) : null
    }>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <UserCog className="h-4 w-4 text-[hsl(var(--brand))]" /> {t("operators.title")} ({operators.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {operators.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center" data-testid="operators-empty">
              <UserCog className="h-10 w-10 text-muted-foreground/50" />
              <p className="mt-3 text-sm text-muted-foreground">{t("operators.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto thin-scroll">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("operators.name")}</TableHead>
                    <TableHead>{t("operators.jobTitle")}</TableHead>
                    <TableHead>{t("operators.linkedUser")}</TableHead>
                    <TableHead>{t("operators.status")}</TableHead>
                    {canManage && <TableHead className="text-right">{t("operators.actions")}</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {operators.map((o) => (
                    <TableRow key={o.id} data-testid={`operator-row-${o.id}`}>
                      <TableCell className="font-medium">{o.name}</TableCell>
                      <TableCell className="text-muted-foreground">{o.title || "-"}</TableCell>
                      <TableCell>
                        {o.linked_user_id ? (
                          <Badge className="badge-ok gap-1"><Link2 className="h-3 w-3" /> {t("operators.linked")}</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">{t("operators.noUser")}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {o.active ? (
                          <Badge className="badge-ok">{t("operators.active")}</Badge>
                        ) : (
                          <Badge className="bg-secondary text-muted-foreground border border-border">{t("operators.inactive")}</Badge>
                        )}
                      </TableCell>
                      {canManage && (
                        <TableCell className="text-right">
                          <Button variant="ghost" size="icon" onClick={() => setDialog({ ...empty, ...o })} data-testid={`operator-edit-${o.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => remove(o.id)} data-testid={`operator-delete-${o.id}`}>
                            <Trash2 className="h-4 w-4 text-muted-foreground" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <OperatorDialog
        key={dialog ? (dialog.id ?? "new") : "closed"}
        dialog={dialog}
        users={users}
        onClose={() => setDialog(null)}
        onSave={save}
      />
    </Layout>
  );
}

function OperatorDialog({ dialog, users, onClose, onSave }) {
  const { t } = useT();
  const [f, setF] = useState(dialog);
  if (!f) return null;
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  return (
    <Dialog open={!!dialog} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{f.id ? t("operators.edit") : t("operators.new")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label>{t("operators.name")}</Label>
            <Input value={f.name} onChange={(e) => set("name", e.target.value)} data-testid="operator-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("operators.jobTitle")}</Label>
            <Input value={f.title || ""} onChange={(e) => set("title", e.target.value)} data-testid="operator-title-input" />
          </div>
          <div className="space-y-1.5">
            <Label>{t("operators.linkedUser")}</Label>
            <Select value={f.linked_user_id || NONE} onValueChange={(v) => set("linked_user_id", v === NONE ? null : v)}>
              <SelectTrigger data-testid="operator-user-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t("operators.noUser")}</SelectItem>
                {users.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name} · {u.role}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t("operators.linkedUserHint")}</p>
          </div>
          <div className="space-y-1.5">
            <Label>{t("operators.notes")}</Label>
            <Textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="operator-notes-input" />
          </div>
          <div className="flex items-center justify-between rounded-lg border border-border p-3">
            <div>
              <Label className="cursor-pointer">{t("operators.active")}</Label>
            </div>
            <Switch checked={!!f.active} onCheckedChange={(v) => set("active", v)} data-testid="operator-active-switch" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t("common.cancel")}</Button>
          <Button onClick={() => onSave(f)} data-testid="operator-save-button">{t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
